/**
 * Practical patterns for the regex tester's library. Each one's name, caveat and sample
 * text live in the widget's strings (keyed by `id`), because they are prose.
 */

export interface LibraryPattern {
  id: LibraryPatternId;
  pattern: string;
  flags: string;
  /** A useful replacement to preview, or "" for none. */
  replacement: string;
}

export type LibraryPatternId =
  | "isoDate"
  | "email"
  | "url"
  | "doi"
  | "isbn"
  | "usPhone"
  | "citation"
  | "doubledWord"
  | "doubleSpace";

export const REGEX_LIBRARY: readonly LibraryPattern[] = [
  {
    id: "isoDate",
    pattern:
      "\\b(?<year>\\d{4})-(?<month>0[1-9]|1[0-2])-(?<day>0[1-9]|[12]\\d|3[01])\\b",
    flags: "g",
    replacement: "$<day>/$<month>/$<year>",
  },
  {
    id: "email",
    pattern: "\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}\\b",
    flags: "g",
    replacement: "",
  },
  {
    id: "url",
    pattern: "\\bhttps?://[^\\s<>\"']*[^\\s<>\"'.,;:!?)]",
    flags: "gi",
    replacement: "",
  },
  {
    // Crossref's recommended pattern for modern DOIs, unanchored to find them in text.
    id: "doi",
    pattern: "\\b10\\.\\d{4,9}/[-._;()/:A-Z0-9]+",
    flags: "gi",
    replacement: "https://doi.org/$&",
  },
  {
    // ISBN-10 or ISBN-13, with or without the ISBN prefix, hyphens or spaces (one per line).
    id: "isbn",
    pattern:
      "^(?:ISBN(?:-1[03])?:? )?(?=[0-9X]{10}$|(?=(?:[0-9]+[- ]){3})[- 0-9X]{13}$|97[89][0-9]{10}$|(?=(?:[0-9]+[- ]){4})[- 0-9]{17}$)(?:97[89][- ]?)?[0-9]{1,5}[- ]?[0-9]+[- ]?[0-9]+[- ]?[0-9X]$",
    flags: "gm",
    replacement: "",
  },
  {
    // North American Numbering Plan: area code 2–9 then 0–8, exchange 2–9.
    id: "usPhone",
    pattern:
      "(?:\\+?1[-. ]?)?\\(?\\b([2-9][0-8][0-9])\\)?[-. ]?([2-9][0-9]{2})[-. ]?([0-9]{4})\\b",
    flags: "g",
    replacement: "($1) $2-$3",
  },
  {
    // APA-style parenthetical citations: (Author, 2020), (A & B, 2020a), (A et al., n.d.).
    id: "citation",
    pattern:
      "\\((\\p{Lu}[\\p{L}'’-]+(?: (?:&|and) \\p{Lu}[\\p{L}'’-]+| et al\\.)?), (\\d{4}[a-z]?|n\\.d\\.)\\)",
    flags: "gu",
    replacement: "",
  },
  {
    id: "doubledWord",
    pattern: "\\b(\\w+)\\s+\\1\\b",
    flags: "gi",
    replacement: "$1",
  },
  {
    id: "doubleSpace",
    pattern: " {2,}",
    flags: "g",
    replacement: " ",
  },
];

export function libraryPattern(id: LibraryPatternId): LibraryPattern {
  return REGEX_LIBRARY.find((entry) => entry.id === id) ?? REGEX_LIBRARY[0];
}
